import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Operaciones } from '../api/operaciones';
import type { Cita, Documento, Hueco, VistaPreviaCambio } from '../api/ops/citas';
import { intentosDelPin } from '../api/ops/tablet';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Fecha } from '../componentes/Fecha';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { Ventana } from '../componentes/Ventana';
import { dinero } from '../logica/dinero';
import { estadoDeError } from '../logica/estados';
import { navegador } from '../logica/navegador';
import { activa, ahoraEn, franjaDe, hm, horaEn, primerNombre, textoDeError, textoServicios, todas } from '../pantallas/agenda/logica';
import { permisosDe } from '../permisos/permisos';
import type { SesionMiembro } from './almacenTablet';
import { guardarEntrega, leerEntrega, vueltaDeFirma, type AlmacenEntrega, type EntregaFirma } from './entregaFirma';
import { Teclado } from './Teclado';
import { AYUDA_FIRMA, T_FIRMA } from './textosFirma';
import './firma.css';

/**
 * «Firma en el centro» (9.13) en la tablet (1.13), con la sesión con PIN de quien la usa: las citas de hoy cuyo
 * consentimiento está pedido o pendiente (y las ya firmadas hoy), de `listAppointments` filtradas por `consent_status`; el
 * profesional ve solo las suyas porque la API le da «lo suyo» (G415). Se puede firmar mientras el documento esté pedido o
 * pendiente (G478) y la cita no se cancela por no firmar (G441).
 *
 * - «Firmar en esta tablet»: `startInPersonSigning` con el `device_id` de la tablet; «Dale la tablet a…» y la clienta firma
 *   en la sesión del proveedor eIDAS (sin nombrarlo, G56), que lee, comprueba su identidad y recoge la firma. A la vuelta
 *   (`?vuelta=1`, solo con https) se espera la firma con `getDocument` y la tablet vuelve con el PIN de quien la entregó
 *   (`startMemberSessionOnDevice`). Mientras está entregada (sessionStorage) no se ve nada del panel.
 * - «Reenviar el enlace» (`resendSignatureLink`, 3 al día: el `409` dice el motivo) y «Cambiar de día»
 *   (`searchAppointmentSlots`, `previewAppointmentModification`, `modifyAppointment` con `If-Match` de `getAppointment`).
 * - Abierta desde la ficha de la cita del panel (`?origen=panel&cita=`): «‹ Panel» vuelve a esa cita.
 */
export interface PropsFirma {
    /** Operaciones con la sesión con PIN de la persona. */
    ops: Operaciones;
    /** Operaciones con la credencial de la tablet (el PIN para volver). */
    opsTablet: Operaciones;
    negocio: string;
    nombreNegocio: string;
    deviceId: string;
    sesion: SesionMiembro;
    zona: string | null;
    /** La ruta con su consulta (`?cita=`, `?origen=panel`, `?vuelta=1`). */
    ruta: string;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    /** «Bloquear»: cierra la sesión y deja la tablet en «Tablet bloqueada». */
    onBloquear: () => void;
    /** El PIN bloqueado al volver: a «¿Quién eres?». */
    onCambiarPersona: () => void;
    /** El PIN de vuelta abre una sesión nueva. */
    onSesion: (s: SesionMiembro) => void;
    almacen: AlmacenEntrega;
    /** Origen de la página (para la vuelta del proveedor). */
    origen?: string;
    ahora?: () => Date;
    /** Espera entre comprobaciones de la firma a la vuelta (las pruebas la acortan). */
    espera?: number;
}

interface Fila {
    cita: Cita;
    nombre: string;
}

type Carga = { tipo: 'cargando' } | { tipo: 'error'; error: unknown } | { tipo: 'sin-permiso' } | { tipo: 'no-contratado' } | { tipo: 'listo'; filas: Fila[]; suyo: boolean };
type Modo = { tipo: 'lista' } | { tipo: 'entregar'; url: string; entrega: EntregaFirma } | { tipo: 'cliente'; entrega: EntregaFirma; firmado: boolean | null } | { tipo: 'pin'; entrega: EntregaFirma; firmado: boolean };
interface Banner {
    ok: boolean;
    titulo: string;
    texto: string;
}

const MAX_PIN = 6;
const MIN_PIN = 4;
const COMPROBACIONES = 6;
const CON_FIRMA = new Set(['requested', 'pending', 'signed']);

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** «sábado 17 de octubre». */
function diaLargo(iso: string): string {
    const [a, m, d] = iso.split('-').map(Number);
    const f = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1));
    return `${DIAS[f.getUTCDay()] ?? ''} ${String(d)} de ${MESES[(m ?? 1) - 1] ?? ''}`;
}

const proDe = (c: Cita) => primerNombre(c.segments[0]?.professional_display_name ?? '');

function chipDe(c: Cita): readonly [string, EstadoChip] {
    return c.consent_status === 'signed' ? [T_FIRMA.chipFirmado, 'confirmed'] : c.consent_status === 'requested' ? [T_FIRMA.chipEnlace, 'pending'] : [T_FIRMA.chipPendiente, 'pending'];
}

export function FirmaEnElCentro(props: PropsFirma) {
    const { ops, opsTablet, negocio, nombreNegocio, deviceId, sesion, zona, ruta, navegar, almacen, espera = 2500 } = props;
    const consulta = useMemo(() => new URLSearchParams(ruta.split('?')[1] ?? ''), [ruta]);
    const desdePanel = consulta.get('origen') === 'panel';
    const citaPedida = consulta.get('cita');
    const vuelta = consulta.get('vuelta') === '1';
    const hoy = ahoraEn(zona, props.ahora?.() ?? new Date()).fecha;
    const yo = primerNombre(sesion.nombre);

    const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [sel, setSel] = useState<string | null>(citaPedida);
    const [modo, setModo] = useState<Modo>(() => {
        const e = leerEntrega(almacen);
        return e === null ? { tipo: 'lista' } : vuelta ? { tipo: 'cliente', entrega: e, firmado: null } : { tipo: 'pin', entrega: e, firmado: false };
    });
    const [banner, setBanner] = useState<Banner | null>(null);
    const [ayuda, setAyuda] = useState(false);
    const [cambiando, setCambiando] = useState(false);

    // ---- Datos: permisos, bloque Firma eIDAS y las citas de hoy con consentimiento ----
    useEffect(() => {
        if (modo.tipo !== 'lista') {
            return;
        }
        let vivo = true;
        const cargar = async (): Promise<Carga> => {
            const permisos = permisosDe(await ops.getMyNavigation(negocio));
            if (!permisos.puede('documentos.firmas.recoger')) {
                return { tipo: 'sin-permiso' };
            }
            const bloques = await ops.listEntitlements(negocio).catch(() => null);
            if (bloques !== null && !bloques.entitlements.some((e) => e.product_code === 'firma_eidas')) {
                return { tipo: 'no-contratado' };
            }
            const citas = (await todas((cursor) => ops.listAppointments(negocio, { from: hoy, to: hoy, cursor, limit: 100 }))).filter((c) => activa(c) && CON_FIRMA.has(c.consent_status) && c.document_id != null);
            const ids = [...new Set(citas.map((c) => c.customer_id).filter((x): x is string => x !== null))];
            const nombres = new Map<string, string>();
            if (ids.length > 0) {
                await ops
                    .listCustomersByIds(negocio, ids)
                    .then((l) => {
                        for (const c of l.data) {
                            nombres.set(c.id, c.display_name);
                        }
                    })
                    .catch(() => undefined);
            }
            return { tipo: 'listo', filas: citas.map((cita) => ({ cita, nombre: cita.customer_id === null ? 'Cliente suprimido' : (nombres.get(cita.customer_id) ?? 'Cliente') })), suyo: permisos.ambito('citas.agenda.ver') === 'own' };
        };
        cargar()
            .then((c) => {
                if (vivo) {
                    setCarga(c);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCarga(e instanceof ErrorApi && e.codigo === 'entitlement_required' ? { tipo: 'no-contratado' } : { tipo: 'error', error: e });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, hoy, intento, modo.tipo]);

    const filas = carga.tipo === 'listo' ? carga.filas : [];
    const pendientes = filas.filter((f) => f.cita.consent_status !== 'signed').sort((a, b) => a.cita.local_time.localeCompare(b.cita.local_time));
    const firmadas = filas.filter((f) => f.cita.consent_status === 'signed');
    const elegida = filas.find((f) => f.cita.id === sel) ?? null;
    // Sin nada elegido, la primera por firmar (en horizontal se ve a la vez que la lista).
    const actual = elegida ?? pendientes[0] ?? null;

    const ponerCita = (c: Cita) => {
        setCarga((x) => (x.tipo === 'listo' ? { ...x, filas: x.filas.map((f) => (f.cita.id === c.id ? { ...f, cita: c } : f)) } : x));
    };

    const salirAlPanel = () => {
        navegar(citaPedida === null ? '/citas' : `/citas?cita=${encodeURIComponent(citaPedida)}&tab=firma`);
    };

    // ---- Modo cliente, entregar y PIN ----
    if (modo.tipo === 'entregar') {
        return (
            <div className="rv-fc rv-fc--entrega">
                <span className="rv-fc-entrega__baldosa" aria-hidden="true">
                    <Icono nombre="tablet-smartphone" />
                </span>
                <h1>{T_FIRMA.entregaTitulo(modo.entrega.cliente)}</h1>
                <p>{T_FIRMA.entregaTexto}</p>
                <div className="rv-fc-entrega__botones">
                    <button
                        type="button"
                        className="rv-fc-b rv-fc-b--rojo"
                        onClick={() => {
                            guardarEntrega(almacen, modo.entrega);
                            navegador.ir(modo.url);
                        }}
                    >
                        <span>{T_FIRMA.empezar}</span>
                        <Icono nombre="arrow-right" />
                    </button>
                    <button type="button" className="rv-fc-b rv-fc-b--claro" onClick={() => { setModo({ tipo: 'lista' }); }}>
                        <Icono nombre="x" />
                        <span>{T_FIRMA.cancelar}</span>
                    </button>
                </div>
            </div>
        );
    }
    if (modo.tipo === 'cliente') {
        return <ModoCliente ops={ops} negocio={negocio} nombreNegocio={nombreNegocio} entrega={modo.entrega} yo={yo} espera={espera} onDevolver={(firmado) => { setModo({ tipo: 'pin', entrega: modo.entrega, firmado }); }} />;
    }
    if (modo.tipo === 'pin') {
        return (
            <PinDeVuelta
                opsTablet={opsTablet}
                sesion={sesion}
                firmado={modo.firmado}
                onBloqueado={() => {
                    guardarEntrega(almacen, null);
                    props.onCambiarPersona();
                }}
                onDentro={(s) => {
                    guardarEntrega(almacen, null);
                    const nombre = modo.entrega.cliente;
                    setSel(modo.entrega.citaId);
                    setBanner(modo.firmado ? { ok: true, titulo: T_FIRMA.haFirmadoTitulo(nombre), texto: T_FIRMA.haFirmadoTexto } : { ok: false, titulo: T_FIRMA.noFirmadoTitulo, texto: T_FIRMA.noFirmadoTexto(nombre) });
                    setModo({ tipo: 'lista' });
                    setCarga({ tipo: 'cargando' });
                    props.onSesion(s);
                    const sinVuelta = new URLSearchParams(consulta);
                    sinVuelta.delete('vuelta');
                    navegar(`/tablet/firmas${sinVuelta.toString() === '' ? '' : `?${sinVuelta.toString()}`}`, true);
                }}
            />
        );
    }

    // ---- Personal: cabecera, lista y ficha ----
    const suyo = carga.tipo === 'listo' && carga.suyo;
    const conFicha = sel !== null && elegida !== null;
    return (
        <div className="rv-fc">
            <header className="rv-fc__cab">
                {desdePanel && (
                    <button type="button" className="rv-fc-b rv-fc-b--suave" onClick={salirAlPanel} aria-label="Volver al panel">
                        <Icono nombre="chevron-left" />
                        <span>{T_FIRMA.panel}</span>
                    </button>
                )}
                <span className="rv-fc__donde">
                    <small>
                        <Icono nombre="signature" />
                        {desdePanel ? T_FIRMA.eyebrowPanel(nombreNegocio) : T_FIRMA.eyebrowTablet(nombreNegocio)}
                    </small>
                    <strong>{cambiando ? T_FIRMA.tituloCambiar : suyo ? T_FIRMA.tituloSuyo : T_FIRMA.titulo}</strong>
                </span>
                <button type="button" className={`rv-fc-ayuda${ayuda ? ' rv-fc-ayuda--on' : ''}`} aria-label={T_FIRMA.ayuda} aria-expanded={ayuda} onClick={() => { setAyuda((a) => !a); }}>
                    ?
                </button>
                <span className="rv-tablet__avatar rv-fc__yo">{sesion.iniciales}</span>
                <span className="rv-fc__nombre">{sesion.nombre}</span>
                <button type="button" className="rv-fc-b rv-fc-b--suave" onClick={props.onBloquear}>
                    <Icono nombre="lock" />
                    <span>{T_FIRMA.bloquear}</span>
                </button>
            </header>
            {ayuda && (
                <section className="rv-fc-ayudas" aria-label={T_FIRMA.ayuda}>
                    {AYUDA_FIRMA.map(([icono, titulo, texto]) => (
                        <div key={titulo} className="rv-fc-ayudas__t">
                            <Icono nombre={icono} />
                            <span>
                                <strong>{titulo}</strong>
                                <span>{texto}</span>
                            </span>
                        </div>
                    ))}
                </section>
            )}
            <main className="rv-fc__cuerpo">
                {carga.tipo !== 'listo' ? (
                    <EstadoFirmas carga={carga} nombreNegocio={nombreNegocio} onReintentar={() => { setCarga({ tipo: 'cargando' }); setIntento((i) => i + 1); }} />
                ) : filas.length === 0 ? (
                    <Tarjeta icono="inbox" fondo="var(--rv-paper)" tinta="var(--rv-ink)" titulo={T_FIRMA.vaciaTitulo} texto={suyo ? T_FIRMA.vaciaTextoSuyo : T_FIRMA.vaciaTexto} />
                ) : cambiando && actual !== null ? (
                    <CambiarDia
                        ops={ops}
                        negocio={negocio}
                        fila={actual}
                        hoy={hoy}
                        onVolver={() => { setCambiando(false); }}
                        onCambiada={(nueva, dia, hora) => {
                            setCambiando(false);
                            setCarga((x) => (x.tipo === 'listo' ? { ...x, filas: x.filas.filter((f) => f.cita.id !== nueva.id) } : x));
                            setSel(null);
                            setBanner({ ok: true, titulo: T_FIRMA.cambiadaTitulo(primerNombre(actual.nombre)), texto: T_FIRMA.cambiadaTexto(diaLargo(dia), hora) });
                        }}
                    />
                ) : (
                    <div className={`rv-fc__dos${conFicha ? ' rv-fc__dos--ficha' : ''}`}>
                        <section className="rv-fc-lista" aria-label={T_FIRMA.firmasDeHoy}>
                            {[
                                [T_FIRMA.porFirmar(pendientes.length), pendientes] as const,
                                [T_FIRMA.firmadosHoy, firmadas] as const,
                            ]
                                .filter(([, l]) => l.length > 0)
                                .map(([titulo, l]) => (
                                    <div key={titulo} className="rv-fc-lista__grupo">
                                        <span className="rv-fc-lista__titulo">{titulo}</span>
                                        {l.map((f) => {
                                            const fr = franjaDe(f.cita);
                                            const [chip, estado] = chipDe(f.cita);
                                            const on = actual?.cita.id === f.cita.id;
                                            return (
                                                <button
                                                    key={f.cita.id}
                                                    type="button"
                                                    className={`rv-fc-fila${on ? ' rv-fc-fila--on' : ''}`}
                                                    aria-pressed={on}
                                                    onClick={() => {
                                                        setSel(f.cita.id);
                                                        setBanner(null);
                                                    }}
                                                >
                                                    <span className="rv-fc-fila__hora">
                                                        <strong>{hm(fr.inicio)}</strong>
                                                        <small>{hm(fr.fin)}</small>
                                                    </span>
                                                    <span className="rv-fc-fila__texto">
                                                        <strong>{f.nombre}</strong>
                                                        <small>{`${textoServicios(f.cita)}${proDe(f.cita) === '' ? '' : ` · con ${proDe(f.cita)}`}`}</small>
                                                    </span>
                                                    <Chip estado={estado}>{chip}</Chip>
                                                    <Icono nombre="chevron-right" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                        </section>
                        {actual !== null && (
                            <FichaFirma
                                key={actual.cita.id}
                                ops={ops}
                                negocio={negocio}
                                deviceId={deviceId}
                                fila={actual}
                                zona={zona}
                                banner={banner}
                                origen={props.origen}
                                ruta={ruta}
                                memberId={sesion.memberId}
                                onVolver={() => {
                                    setSel(null);
                                    setBanner(null);
                                }}
                                onBanner={setBanner}
                                onCita={ponerCita}
                                onEntregar={(url, entrega) => {
                                    setModo({ tipo: 'entregar', url, entrega });
                                }}
                                onCambiar={() => {
                                    setSel(actual.cita.id);
                                    setCambiando(true);
                                }}
                            />
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

// ---- Estados de toda la pantalla ----

function Tarjeta({ icono, fondo, tinta, titulo, texto, children }: { icono: NombreIcono; fondo: string; tinta: string; titulo: string; texto: string; children?: ReactNode }) {
    return (
        <div className="rv-fc-estado">
            <span className="rv-fc-estado__baldosa" style={{ background: fondo, color: tinta }} aria-hidden="true">
                <Icono nombre={icono} />
            </span>
            <h1>{titulo}</h1>
            <p>{texto}</p>
            {children}
        </div>
    );
}

function EstadoFirmas({ carga, nombreNegocio, onReintentar }: { carga: Exclude<Carga, { tipo: 'listo' }>; nombreNegocio: string; onReintentar: () => void }) {
    switch (carga.tipo) {
        case 'cargando':
            return (
                <div className="rv-fc-estado" aria-busy="true" aria-label="Cargando">
                    {[0, 1, 2].map((i) => (
                        <span key={i} className="rv-fc-esqueleto" style={{ animationDelay: `${String(i * 80)}ms` }} />
                    ))}
                </div>
            );
        case 'sin-permiso':
            return <Tarjeta icono="lock" fondo="var(--rv-ink)" tinta="#fff" titulo={T_FIRMA.sinPermisoTitulo} texto={T_FIRMA.sinPermisoTexto(nombreNegocio)} />;
        case 'no-contratado':
            return (
                <Tarjeta icono="puzzle" fondo="var(--rv-red)" tinta="#fff" titulo={T_FIRMA.noContratadoTitulo} texto={T_FIRMA.noContratadoTexto}>
                    <ul className="rv-fc-estado__lista">
                        {T_FIRMA.noContratadoIncluye.map((t) => (
                            <li key={t}>
                                <Icono nombre="check" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </Tarjeta>
            );
        case 'error': {
            const codigo = estadoDeError(carga.error).codigoSoporte;
            return (
                <Tarjeta icono="triangle-alert" fondo="var(--rv-red-soft)" tinta="var(--rv-red)" titulo={T_FIRMA.errorTitulo} texto={T_FIRMA.errorTexto}>
                    <button type="button" className="rv-fc-b rv-fc-b--tinta" onClick={onReintentar}>
                        <Icono nombre="refresh-cw" />
                        <span>{T_FIRMA.reintentar}</span>
                    </button>
                    {codigo != null && <small className="rv-fc-estado__codigo">{`Código para soporte: ${codigo}`}</small>}
                </Tarjeta>
            );
        }
    }
}

// ---- Ficha de la firma ----

interface PropsFicha {
    ops: Operaciones;
    negocio: string;
    deviceId: string;
    fila: Fila;
    zona: string | null;
    banner: Banner | null;
    origen: string | undefined;
    ruta: string;
    memberId: string;
    onVolver: () => void;
    onBanner: (b: Banner | null) => void;
    onCita: (c: Cita) => void;
    onEntregar: (url: string, entrega: EntregaFirma) => void;
    onCambiar: () => void;
}

function FichaFirma({ ops, negocio, deviceId, fila, zona, banner, origen, ruta, memberId, onVolver, onBanner, onCita, onEntregar, onCambiar }: PropsFicha) {
    const { cita, nombre } = fila;
    const pila = primerNombre(nombre);
    const [doc, setDoc] = useState<Documento | null>(null);
    // Las horas de la caja (envío, recordatorio, plazo, firma) salen del documento: hasta tenerlo no se pinta su texto.
    const [docFallo, setDocFallo] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState('');
    const [reenviar, setReenviar] = useState(false);
    const [reenviando, setReenviando] = useState(false);
    const [errorReenvio, setErrorReenvio] = useState('');

    useEffect(() => {
        if (cita.document_id == null) {
            return;
        }
        let vivo = true;
        ops.getDocument(negocio, cita.document_id)
            .then((d) => {
                if (vivo) {
                    setDoc(d);
                }
            })
            .catch(() => {
                if (vivo) {
                    setDocFallo(true);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, cita.document_id]);

    const fr = franjaDe(cita);
    const firmada = cita.consent_status === 'signed';
    const [chip, estadoChip] = chipDe(cita);
    const hora = (iso: string | null | undefined) => (iso == null ? '' : horaEn(iso, zona));
    // G873: la hora del último envío del enlace y los reenvíos de hoy los da la API.
    const remoto = [...(doc?.requests ?? [])].reverse().find((r) => r.channel === 'remote_link');
    const enviado = hora(remoto?.sent_at ?? doc?.created_at);
    const caja: readonly [NombreIcono, string, string, string, string] = firmada
        ? ['file-check', 'var(--rv-paper)', 'var(--rv-ink)', T_FIRMA.firmadoA(hora(doc?.signed_at)), T_FIRMA.firmadoTexto]
        : cita.consent_status === 'requested'
          ? ['send', 'var(--rv-warning-soft)', 'var(--rv-warning-ink)', T_FIRMA.enlaceTitulo, T_FIRMA.enlaceTexto(enviado, hora(doc?.sign_by))]
          : doc?.pending_reason === 'declined'
            ? ['x', 'var(--rv-warning-soft)', 'var(--rv-warning-ink)', T_FIRMA.rechazoTitulo, T_FIRMA.rechazoTexto(enviado)]
            : ['clock', 'var(--rv-warning-soft)', 'var(--rv-warning-ink)', T_FIRMA.plazoTitulo, T_FIRMA.plazoTexto(enviado, hora(remoto?.reminder_sent_at), hora(doc?.sign_by))];

    const firmar = () => {
        if (cita.document_id == null || ocupado) {
            return;
        }
        const documentId = cita.document_id;
        setOcupado(true);
        setError('');
        const vuelta = origen === undefined ? null : vueltaDeFirma(origen, ruta);
        ops.startInPersonSigning(negocio, documentId, { device_id: deviceId, ...(vuelta === null ? {} : { return_url: vuelta }) })
            .then((s) => {
                setOcupado(false);
                onEntregar(s.url, { documentId, citaId: cita.id, cliente: pila, memberId });
            })
            .catch((e: unknown) => {
                setOcupado(false);
                if (e instanceof ErrorApi && e.estado === 409) {
                    // Ya firmado (desde su móvil, mientras tanto).
                    onCita({ ...cita, consent_status: 'signed' });
                    onBanner({ ok: true, titulo: T_FIRMA.yaFirmoTitulo(pila), texto: T_FIRMA.yaFirmoTexto });
                    return;
                }
                setError(e instanceof ErrorApi && e.estado === 502 ? T_FIRMA.proveedorCaido : textoDeError(e));
            });
    };

    const hacerReenvio = () => {
        if (cita.document_id == null) {
            return;
        }
        setReenviando(true);
        setErrorReenvio('');
        const documentId = cita.document_id;
        ops.resendSignatureLink(negocio, documentId)
            .then(() => {
                setReenviar(false);
                // La hora del envío y los reenvíos de hoy, de nuevo de la API (G873).
                ops.getDocument(negocio, documentId).then(setDoc).catch(() => undefined);
                onBanner({ ok: true, titulo: T_FIRMA.reenviadoTitulo, texto: T_FIRMA.reenviadoTexto(pila, hora(doc?.sign_by)) });
            })
            .catch((e: unknown) => {
                setErrorReenvio(textoDeError(e));
            })
            .finally(() => {
                setReenviando(false);
            });
    };

    const lineas: [NombreIcono, string][] = [
        ['clock', T_FIRMA.hoyDe(hm(fr.inicio), hm(fr.fin), proDe(cita))],
        ['scissors', textoServicios(cita)],
        ['file-text', doc?.title ?? 'Consentimiento'],
    ];

    return (
        <section className="rv-fc-ficha" aria-labelledby="rv-fc-ficha-t">
            <button type="button" className="rv-fc-ficha__volver" onClick={onVolver}>
                <Icono nombre="chevron-left" />
                <span>{T_FIRMA.firmasDeHoy}</span>
            </button>
            {banner !== null && (
                <div role="status" className={`rv-fc-banner${banner.ok ? '' : ' rv-fc-banner--aviso'}`}>
                    <Icono nombre={banner.ok ? 'circle-check' : 'circle-alert'} />
                    <span>
                        <strong>{banner.titulo}</strong>
                        <span>{banner.texto}</span>
                    </span>
                </div>
            )}
            <div className="rv-fc-ficha__tarjeta">
                <div className="rv-fc-ficha__cab">
                    <span className="rv-tablet__avatar rv-fc-ficha__avatar">{nombre.split(/\s+/).map((p) => p.charAt(0)).slice(0, 2).join('').toUpperCase()}</span>
                    <span>
                        <h1 id="rv-fc-ficha-t">{nombre}</h1>
                        <Chip estado={estadoChip}>{chip}</Chip>
                    </span>
                </div>
                <ul className="rv-fc-ficha__lineas">
                    {lineas.map(([icono, texto]) => (
                        <li key={icono}>
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </li>
                    ))}
                </ul>
                {doc === null && cita.document_id != null && !docFallo ? (
                    <span className="rv-fc-esqueleto" aria-busy="true" aria-label={T_FIRMA.cargandoDocumento} />
                ) : (
                    <div className="rv-fc-caja" style={{ background: caja[1], color: caja[2] }}>
                        <Icono nombre={caja[0]} />
                        <span>
                            <strong>{caja[3]}</strong>
                            {/* Sin el documento (no se ha podido leer) no hay horas: solo el título y, sin firmar, que no se cancela. */}
                            <span>{doc === null ? (firmada ? '' : T_FIRMA.noSeCancela.trim()) : caja[4] + (firmada ? '' : T_FIRMA.noSeCancela)}</span>
                        </span>
                    </div>
                )}
                {error !== '' && (
                    <div role="alert" className="rv-fc-error">
                        <Icono nombre="circle-alert" />
                        <span>{error}</span>
                    </div>
                )}
                {!firmada && (
                    <div className="rv-fc-ficha__acciones">
                        <button type="button" className="rv-fc-b rv-fc-b--rojo rv-fc-b--grande" aria-busy={ocupado} onClick={firmar}>
                            {ocupado ? <span className="rv-fc-gira" /> : <Icono nombre="signature" />}
                            <span>{ocupado ? T_FIRMA.preparando : T_FIRMA.firmarAqui}</span>
                        </button>
                        <div className="rv-fc-ficha__dos">
                            {cita.consent_status === 'requested' && (
                                <button type="button" className="rv-fc-b rv-fc-b--claro" onClick={() => { setErrorReenvio(''); setReenviar(true); }}>
                                    <Icono nombre="send" />
                                    <span>{T_FIRMA.reenviar}</span>
                                </button>
                            )}
                            {cita.actions.can_modify && (
                                <button type="button" className="rv-fc-b rv-fc-b--claro" onClick={onCambiar}>
                                    <Icono nombre="calendar-clock" />
                                    <span>{T_FIRMA.cambiarDia}</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Ventana
                abierta={reenviar}
                titulo={T_FIRMA.reenviarPregunta(pila)}
                texto={T_FIRMA.reenviarTexto(remoto?.resends_today ?? 0)}
                accion={T_FIRMA.reenviarAccion}
                accionIcono="send"
                ocupado={reenviando}
                ocupadoTexto={T_FIRMA.reenviando}
                onAccion={hacerReenvio}
                onCerrar={() => { if (!reenviando) { setReenviar(false); } }}
            >
                {errorReenvio !== '' && (
                    <p role="alert" className="rv-fc-error">
                        <Icono nombre="circle-alert" />
                        <span>{errorReenvio}</span>
                    </p>
                )}
            </Ventana>
        </section>
    );
}

// ---- Cambiar de día ----

function CambiarDia({ ops, negocio, fila, hoy, onVolver, onCambiada }: { ops: Operaciones; negocio: string; fila: Fila; hoy: string; onVolver: () => void; onCambiada: (c: Cita, dia: string, hora: string) => void }) {
    const { cita, nombre } = fila;
    const pila = primerNombre(nombre);
    const pro = proDe(cita);
    const proId = cita.segments[0]?.professional_id ?? null;
    const [mismo, setMismo] = useState(true);
    const [dia, setDia] = useState(hoy);
    const [huecos, setHuecos] = useState<{ slots: Hueco[]; cerrado: boolean } | null>(null);
    const [buscar, setBuscar] = useState(0);
    const [elegido, setElegido] = useState<Hueco | null>(null);
    const [vista, setVista] = useState<VistaPreviaCambio | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [aviso, setAviso] = useState('');
    const servicios = useMemo(
        () =>
            cita.segments.map((s) => {
                const answers = s.answers.map((a) => (a.option_id != null ? { question_id: a.question_id, option_ids: [a.option_id] } : a.number_value != null ? { question_id: a.question_id, number_value: a.number_value } : { question_id: a.question_id, text_value: a.text_value ?? '' }));
                return answers.length === 0 ? { service_id: s.service_id } : { service_id: s.service_id, answers };
            }),
        [cita.segments],
    );
    const proPedido = mismo ? proId : null;

    useEffect(() => {
        let vivo = true;
        ops.searchAppointmentSlots(negocio, { services: servicios, professional_id: proPedido, mode: 'list', from: dia, to: dia, exclude_appointment_id: cita.id })
            .then((r) => {
                if (vivo) {
                    setHuecos({ slots: r.slots, cerrado: r.unavailable_reason === 'closed' });
                }
            })
            .catch(() => {
                if (vivo) {
                    setHuecos({ slots: [], cerrado: false });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, servicios, proPedido, dia, cita.id, buscar]);

    const elegir = (h: Hueco) => {
        setElegido(h);
        setAviso('');
        setMotivo('');
        ops.previewAppointmentModification(negocio, cita.id, { local_date: h.local_date, local_time: h.local_time, professional_id: proPedido })
            .then((v) => {
                if (!v.available) {
                    setElegido(null);
                    setAviso(T_FIRMA.yaNoLibre);
                    setBuscar((n) => n + 1);
                    return;
                }
                setVista(v);
            })
            .catch((e: unknown) => {
                setElegido(null);
                setAviso(textoDeError(e));
            });
    };

    const cambiar = () => {
        if (elegido === null) {
            return;
        }
        setOcupado(true);
        const cambio = { local_date: elegido.local_date, local_time: elegido.local_time, professional_id: proPedido, ...(motivo.trim() === '' ? {} : { reason: motivo.trim() }) };
        ops.getAppointment(negocio, cita.id)
            .then((leida) => ops.modifyAppointment(negocio, cita.id, cambio, leida.version ?? `"${cita.id}.${String(cita.version)}"`))
            .then((r) => {
                setVista(null);
                onCambiada(r.datos, elegido.local_date, elegido.local_time);
            })
            .catch((e: unknown) => {
                setVista(null);
                setElegido(null);
                if (e instanceof ErrorApi && e.estado === 409) {
                    setAviso(T_FIRMA.yaNoLibre);
                    setBuscar((n) => n + 1);
                    return;
                }
                setAviso(textoDeError(e));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const importe = vista === null ? '' : vista.payment_effect === 'none' || vista.difference === undefined ? T_FIRMA.sinCambios : dinero(vista.difference);
    return (
        <section className="rv-fc-cambiar" aria-labelledby="rv-fc-cambiar-t">
            <button type="button" className="rv-fc-ficha__volver rv-fc-ficha__volver--siempre" onClick={onVolver}>
                <Icono nombre="chevron-left" />
                <span>{T_FIRMA.volverFirma}</span>
            </button>
            <div className="rv-fc-ficha__tarjeta">
                <h1 id="rv-fc-cambiar-t">{T_FIRMA.cambiarTitulo(pila)}</h1>
                <p className="rv-fc-cambiar__sub">{T_FIRMA.cambiarSub(cita.local_time.slice(0, 5), textoServicios(cita), pro)}</p>
                <div role="radiogroup" aria-label={T_FIRMA.conQuien} className="rv-fc-cambiar__quien">
                    {([[true, T_FIRMA.con(pro), 'user'], [false, T_FIRMA.cualquiera, 'shuffle']] as const).map(([valor, texto, icono]) => (
                        <button
                            key={texto}
                            type="button"
                            role="radio"
                            aria-checked={mismo === valor}
                            className={`rv-fc-b ${mismo === valor ? 'rv-fc-b--tinta' : 'rv-fc-b--claro'}`}
                            onClick={() => {
                                setMismo(valor);
                                setElegido(null);
                            }}
                        >
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </button>
                    ))}
                </div>
                <Fecha etiqueta={T_FIRMA.dia} valor={dia} min={hoy} onChange={(v) => { if (v !== '') { setDia(v); setElegido(null); setHuecos(null); } }} />
                {aviso !== '' && (
                    <p role="alert" className="rv-fc-error">
                        <Icono nombre="circle-alert" />
                        <span>{aviso}</span>
                    </p>
                )}
                {huecos === null ? (
                    <span className="rv-fc-esqueleto" aria-busy="true" />
                ) : huecos.slots.length > 0 ? (
                    <div role="radiogroup" aria-label={T_FIRMA.horasLibres} className="rv-fc-cambiar__horas">
                        {huecos.slots.map((h) => {
                            const on = elegido?.starts_at === h.starts_at;
                            return (
                                <button key={h.starts_at} type="button" role="radio" aria-checked={on} className={`rv-fc-hora${on ? ' rv-fc-hora--on' : ''}`} onClick={() => { elegir(h); }}>
                                    {h.local_time.slice(0, 5)}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="rv-fc-cambiar__vacio">
                        <Icono nombre="calendar-x" />
                        <span>
                            <strong>{huecos.cerrado ? T_FIRMA.cerrado : T_FIRMA.sinHueco}</strong> {T_FIRMA.otroDia}
                        </span>
                    </div>
                )}
                <p className="rv-fc-cambiar__nota">
                    <Icono nombre="info" />
                    <span>{T_FIRMA.notaCambiar}</span>
                </p>
            </div>
            <Ventana
                abierta={vista !== null && elegido !== null}
                titulo={elegido === null ? '' : T_FIRMA.cambiarPregunta(pila, diaLargo(elegido.local_date), elegido.local_time.slice(0, 5))}
                texto={T_FIRMA.cambiarTexto(mismo && pro !== '' ? pro : null)}
                accion={T_FIRMA.cambiarAccion}
                accionIcono="calendar-clock"
                ocupado={ocupado}
                ocupadoTexto={T_FIRMA.cambiando}
                onAccion={cambiar}
                onCerrar={() => {
                    if (!ocupado) {
                        setVista(null);
                        setElegido(null);
                    }
                }}
            >
                {elegido !== null && (
                    <dl className="rv-fc-filas">
                        <div>
                            <dt>{T_FIRMA.ahora}</dt>
                            <dd style={{ textDecoration: 'line-through' }}>{`Hoy, ${cita.local_time.slice(0, 5)}`}</dd>
                        </div>
                        <div>
                            <dt>{T_FIRMA.nueva}</dt>
                            <dd>{`${diaLargo(elegido.local_date)}, ${elegido.local_time.slice(0, 5)}`}</dd>
                        </div>
                        <div>
                            <dt>{T_FIRMA.importe}</dt>
                            <dd>{importe}</dd>
                        </div>
                    </dl>
                )}
                <Campo etiqueta={T_FIRMA.motivo} valor={motivo} filas={2} max={500} ayuda={T_FIRMA.motivoAyuda} onChange={setMotivo} />
            </Ventana>
        </section>
    );
}

// ---- Modo cliente: la vuelta del proveedor ----

function ModoCliente({ ops, negocio, nombreNegocio, entrega, yo, espera, onDevolver }: { ops: Operaciones; negocio: string; nombreNegocio: string; entrega: EntregaFirma; yo: string; espera: number; onDevolver: (firmado: boolean) => void }) {
    const [firmado, setFirmado] = useState<boolean | null>(null);
    useEffect(() => {
        let vivo = true;
        let t: ReturnType<typeof setTimeout> | undefined;
        const leer = (n: number) => {
            ops.getDocument(negocio, entrega.documentId)
                .then((d) => {
                    if (!vivo) {
                        return;
                    }
                    if (d.status === 'signed') {
                        setFirmado(true);
                    } else if (n + 1 < COMPROBACIONES) {
                        // El aviso del proveedor puede tardar unos segundos.
                        t = setTimeout(() => { leer(n + 1); }, espera);
                    } else {
                        setFirmado(false);
                    }
                })
                .catch(() => {
                    if (vivo) {
                        setFirmado(false);
                    }
                });
        };
        leer(0);
        return () => {
            vivo = false;
            if (t !== undefined) {
                clearTimeout(t);
            }
        };
    }, [ops, negocio, entrega.documentId, espera]);

    return (
        <div className="rv-fc-cliente">
            <header className="rv-fc-cliente__cab">
                <span className="rv-fc-cliente__inicial" aria-hidden="true">
                    {nombreNegocio.charAt(0).toUpperCase()}
                </span>
                <span className="rv-fc__donde">
                    <strong>{nombreNegocio}</strong>
                    <small>{T_FIRMA.firmaDeUnDocumento}</small>
                </span>
                {firmado === null && (
                    <button type="button" className="rv-fc-b rv-fc-b--claro" onClick={() => { onDevolver(false); }}>
                        <Icono nombre="undo-2" />
                        <span>{T_FIRMA.devolver}</span>
                    </button>
                )}
            </header>
            <main className="rv-fc-cliente__cuerpo">
                <div className="rv-fc-estado rv-fc-estado--claro">
                    <span className="rv-fc-estado__baldosa" style={{ background: firmado === true ? 'var(--rv-red)' : 'var(--rv-paper)', color: firmado === true ? '#fff' : 'var(--rv-ink)' }} aria-hidden="true">
                        {firmado === null ? <span className="rv-fc-gira" /> : <Icono nombre={firmado ? 'check' : 'circle-alert'} />}
                    </span>
                    <h1>{firmado === null ? T_FIRMA.guardandoTitulo : firmado ? T_FIRMA.graciasTitulo(entrega.cliente) : T_FIRMA.sinTerminarTitulo}</h1>
                    <p>{firmado === null ? T_FIRMA.guardandoTexto : firmado ? T_FIRMA.graciasTexto(yo) : T_FIRMA.sinTerminarTexto(yo)}</p>
                    {firmado !== null && (
                        <button type="button" className="rv-fc-b rv-fc-b--tinta rv-fc-b--grande" onClick={() => { onDevolver(firmado); }}>
                            <Icono nombre="undo-2" />
                            <span>{T_FIRMA.devolverA(yo)}</span>
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
}

// ---- PIN para volver a la sesión ----

function PinDeVuelta({ opsTablet, sesion, firmado, onBloqueado, onDentro }: { opsTablet: Operaciones; sesion: SesionMiembro; firmado: boolean; onBloqueado: () => void; onDentro: (s: SesionMiembro) => void }) {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [ocupado, setOcupado] = useState(false);

    const comprobar = useCallback(
        (valor: string) => {
            setOcupado(true);
            opsTablet
                .startMemberSessionOnDevice(sesion.memberId, valor)
                .then((t) => {
                    onDentro({ ...sesion, token: t.access_token });
                })
                .catch((e: unknown) => {
                    setOcupado(false);
                    setPin('');
                    if (e instanceof ErrorApi && e.codigo === 'conflict') {
                        onBloqueado();
                        return;
                    }
                    // G873: los intentos que quedan los da la API (`attempts_remaining`); el panel no cuenta.
                    const quedan = intentosDelPin(e);
                    if (quedan === undefined) {
                        setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : textoDeError(e));
                        return;
                    }
                    setError(quedan === null ? T_FIRMA.pinMaloSinCifra : T_FIRMA.pinMalo(quedan));
                });
        },
        [opsTablet, sesion, onBloqueado, onDentro],
    );
    const digito = useCallback(
        (d: string) => {
            if (ocupado) {
                return;
            }
            setError('');
            setPin((p) => (p.length >= MAX_PIN ? p : p + d));
        },
        [ocupado],
    );
    const borrar = useCallback(() => {
        setPin((p) => p.slice(0, -1));
    }, []);
    const confirmar = useMemo(() => ({ etiqueta: 'Entrar con este PIN', icono: 'arrow-right' as const, activo: pin.length >= MIN_PIN && !ocupado, accion: () => { comprobar(pin); } }), [pin, ocupado, comprobar]);

    return (
        <div className="rv-fc rv-fc--pin">
            <div className="rv-tablet__dos">
                <div className="rv-tablet__izq rv-tablet__izq--centro">
                    <span className="rv-tablet__avatar rv-tablet__avatar--grande">{sesion.iniciales}</span>
                    <h1 className="rv-fc-pin__titulo">{T_FIRMA.hola(primerNombre(sesion.nombre))}</h1>
                    <p className="rv-fc-pin__sub">{T_FIRMA.escribePin}</p>
                    <div className={`rv-tablet__puntos rv-fc-pin__puntos${error !== '' ? ' rv-tablet__puntos--error' : ''}`} role="status" aria-label={`${String(pin.length)} de ${String(MAX_PIN)} cifras como máximo`}>
                        {Array.from({ length: Math.max(MIN_PIN, pin.length) }, (_, i) => (
                            <span key={i} className={i < pin.length ? 'rv-tablet__punto--lleno' : undefined} />
                        ))}
                    </div>
                    {error !== '' && (
                        <p role="alert" className="rv-tablet__error">
                            <Icono nombre="circle-alert" />
                            {error}
                        </p>
                    )}
                    <p className="rv-fc-pin__nota">{firmado ? T_FIRMA.notaFirmada : T_FIRMA.notaSinFirmar}</p>
                </div>
                <Teclado onDigito={digito} onBorrar={borrar} confirmar={confirmar} />
            </div>
        </div>
    );
}
